import {
  useApiClient,
  type PhcPlatformRestEndpoints,
} from '@lifeomic/phc-web-toolkit/src/hooks/useRestAPIHooks';
import { useMutation } from '@tanstack/react-query';
import React from 'react';

import {
  waitForDocumentUpdateConfirmation,
  type DocumentUpdateConfirmationResult,
} from './confirmation';

export const FILE_VALIDATION_RESOLUTIONS_ROUTE =
  '/v1/member-operations/file-validation/resolutions';
export const FILE_VALIDATION_RESOLUTIONS_ENDPOINT =
  `POST ${FILE_VALIDATION_RESOLUTIONS_ROUTE}` as const;

export type FileValidationResolutionCommand =
  PhcPlatformRestEndpoints[typeof FILE_VALIDATION_RESOLUTIONS_ENDPOINT]['Request'];

export type FileValidationResolutionStatus =
  | 'idle'
  | 'submitting'
  | 'confirming'
  | 'confirmed'
  | 'timeout'
  | 'error'
  | 'confirmation-error';

type FileValidationResolutionMutationVariables = {
  command: FileValidationResolutionCommand;
  attemptId: number;
};

const toResolutionStatus = (
  result: DocumentUpdateConfirmationResult,
): FileValidationResolutionStatus =>
  result.status === 'confirmed'
    ? 'confirmed'
    : result.status === 'timeout'
    ? 'timeout'
    : 'confirmation-error';

export const useFileValidationResolutionCommand = () => {
  const client = useApiClient();
  const activeAttemptId = React.useRef(0);
  const [resolutionStatus, setResolutionStatus] =
    React.useState<FileValidationResolutionStatus>('idle');
  const [confirmationResult, setConfirmationResult] =
    React.useState<DocumentUpdateConfirmationResult>();

  const mutation = useMutation<
    DocumentUpdateConfirmationResult,
    unknown,
    FileValidationResolutionMutationVariables
  >({
    mutationFn: async ({ command, attemptId }) => {
      await client.request(FILE_VALIDATION_RESOLUTIONS_ENDPOINT, command);

      if (activeAttemptId.current === attemptId) {
        setResolutionStatus('confirming');
      }

      return waitForDocumentUpdateConfirmation({
        documentReferenceId: command.documentReferenceId,
      });
    },
  });
  const {
    mutateAsync: runResolutionMutation,
    reset: resetResolutionMutation,
    variables: mutationVariables,
  } = mutation;

  const mutateAsync = React.useCallback(
    async (command: FileValidationResolutionCommand) => {
      const attemptId = activeAttemptId.current + 1;
      activeAttemptId.current = attemptId;
      setConfirmationResult(undefined);
      setResolutionStatus('submitting');

      try {
        const result = await runResolutionMutation({ command, attemptId });

        if (activeAttemptId.current === attemptId) {
          setConfirmationResult(result);
          setResolutionStatus(toResolutionStatus(result));
        }

        return result;
      } catch (error) {
        if (activeAttemptId.current === attemptId) {
          setResolutionStatus('error');
        }

        throw error;
      }
    },
    [runResolutionMutation],
  );

  const reset = React.useCallback(() => {
    activeAttemptId.current += 1;
    setConfirmationResult(undefined);
    setResolutionStatus('idle');
    resetResolutionMutation();
  }, [resetResolutionMutation]);

  const isResolving =
    resolutionStatus === 'submitting' || resolutionStatus === 'confirming';
  const variables = mutationVariables?.command;

  return {
    data: confirmationResult,
    isError: resolutionStatus === 'error',
    isLoading: isResolving,
    isResolved: resolutionStatus === 'confirmed',
    isResolving,
    mutateAsync,
    reset,
    resolutionStatus,
    status: resolutionStatus,
    variables,
  };
};
